import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layers, Plus, Trash2 } from 'lucide-react';
import { useFeedingGroups } from '../../../contexts/FeedingGroupsContext';
import { useToast } from '../../../contexts/ToastContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Card from '../../../components/ui/Card';
import { STOCK_INIT_LEVELS, ROUTES, VALIDATION } from '../../../config/constants';
import { formatGrams } from '../../../lib/format';

/**
 * Form independiente de creación de grupo de alimentación.
 * Vive en /groups/new. Se puede invocar:
 *  - Desde inventario (botón "Crear grupo").
 *  - Desde el form de mascota: ?returnTo=/pets/new vuelve a creación de mascota
 *    con el grupo recién creado preseleccionado (?selectGroup=<id>).
 *
 * Stage 2: el grupo se crea con 0..N dispensers. Cada dispenser tiene nombre,
 * capacidad y nivel inicial (0/25/50/75/100). Si no se agregan dispensers, el
 * grupo opera en "modo sin dispenser" (la mascota come directo de mainStock).
 */

const STOCK_LEVELS = [
  { value: STOCK_INIT_LEVELS.EMPTY, label: 'Vacío' },
  { value: STOCK_INIT_LEVELS.QUARTER, label: '25%' },
  { value: STOCK_INIT_LEVELS.HALF, label: '50%' },
  { value: STOCK_INIT_LEVELS.THREE_QUARTERS, label: '75%' },
  { value: STOCK_INIT_LEVELS.FULL, label: 'Lleno' },
];

const FIELD_ERROR_MAP = {
  INVALID_NAME: 'name',
  INVALID_MAIN_STOCK: 'mainStockGr',
  TOO_MANY_DISPENSERS: 'dispensers',
};

// Genera un id estable solo dentro del form (NO va a Firestore — el service
// asigna ids reales). Lo usamos como key de React.
let formIdCounter = 0;
const nextFormId = () => `f${++formIdCounter}_${Date.now().toString(36)}`;

const makeEmptyDispenser = () => ({
  formId: nextFormId(),
  name: '',
  capacityGr: '',
  initialLevel: 0,
});

const GroupForm = () => {
  const { createFeedingGroup } = useFeedingGroups();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const returnTo = params.get('returnTo');

  const [name, setName] = useState('');
  const [dispensers, setDispensers] = useState([]);
  const [mainStockInitialGr, setMainStockInitialGr] = useState('');

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const addDispenser = () => {
    if (dispensers.length >= VALIDATION.MAX_DISPENSERS_PER_GROUP) {
      toast.error(`Máximo ${VALIDATION.MAX_DISPENSERS_PER_GROUP} dispensers por grupo`);
      return;
    }
    setDispensers((prev) => [...prev, makeEmptyDispenser()]);
  };

  const removeDispenser = (formId) => {
    setDispensers((prev) => prev.filter((d) => d.formId !== formId));
  };

  const updateDispenser = (formId, patch) => {
    setDispensers((prev) =>
      prev.map((d) => (d.formId === formId ? { ...d, ...patch } : d))
    );
  };

  const validateDispensers = () => {
    const dispErrors = {};
    dispensers.forEach((d) => {
      const e = {};
      if (!d.name.trim()) {
        e.name = 'Requerido';
      } else if (d.name.length > VALIDATION.DISPENSER_NAME_MAX) {
        e.name = `Máx ${VALIDATION.DISPENSER_NAME_MAX} chars`;
      }
      const capNum = Number(d.capacityGr);
      if (!d.capacityGr || !Number.isInteger(capNum)) {
        e.capacityGr = 'Inválido';
      } else if (
        capNum < VALIDATION.DISPENSER_CAPACITY_MIN_GR ||
        capNum > VALIDATION.DISPENSER_CAPACITY_MAX_GR
      ) {
        e.capacityGr = `Entre ${VALIDATION.DISPENSER_CAPACITY_MIN_GR}g y ${formatGrams(VALIDATION.DISPENSER_CAPACITY_MAX_GR)}`;
      }
      if (Object.keys(e).length > 0) {
        dispErrors[d.formId] = e;
      }
    });
    return dispErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrors({});

    if (!name.trim()) {
      setErrors({ name: 'El nombre es requerido' });
      return;
    }

    const dispErrors = validateDispensers();
    if (Object.keys(dispErrors).length > 0) {
      setErrors({ dispensersByFormId: dispErrors });
      return;
    }

    const dispensersPayload = dispensers.map((d) => {
      const capacity = Number(d.capacityGr);
      const stock = Math.round(capacity * d.initialLevel);
      return {
        name: d.name.trim(),
        capacityGr: capacity,
        stockGr: stock,
      };
    });

    try {
      setSubmitting(true);
      const { groupId } = await createFeedingGroup({
        name,
        dispensers: dispensersPayload,
        mainStockGr: Number(mainStockInitialGr) || 0,
      });
      toast.success(`Grupo "${name.trim()}" creado.`);

      if (returnTo) {
        const sep = returnTo.includes('?') ? '&' : '?';
        navigate(`${returnTo}${sep}selectGroup=${groupId}`, { replace: true });
      } else {
        navigate(ROUTES.INVENTORY, { replace: true });
      }
    } catch (err) {
      const field = FIELD_ERROR_MAP[err.code];
      if (field) {
        setErrors({ [field]: err.message });
      } else {
        toast.error(err.message || 'No se pudo crear el grupo.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (returnTo) {
      navigate(returnTo, { replace: true });
    } else {
      navigate(ROUTES.INVENTORY, { replace: true });
    }
  };

  const dispErrorsMap = errors.dispensersByFormId || {};

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <Card>
        <div className="flex items-start gap-3 mb-4">
          <div className="rounded-lg bg-brand-100 p-2.5">
            <Layers className="h-5 w-5 text-brand-700" />
          </div>
          <div className="flex-1">
            <h2 className="font-semibold text-slate-900">Datos del grupo</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Un grupo tiene una bolsa principal compartida y 0 o más dispensers.
              Las mascotas asignadas comparten todo el stock del grupo.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Input
            label="Nombre del grupo"
            placeholder="Ej: Cocina, Patio, Comida especial..."
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={errors.name}
            maxLength={40}
            autoFocus
          />

          <Input
            label="Bolsa principal de reserva (gramos)"
            type="number"
            inputMode="numeric"
            placeholder="Ej: 5000"
            value={mainStockInitialGr}
            onChange={(e) => setMainStockInitialGr(e.target.value)}
            error={errors.mainStockGr}
            hint={
              dispensers.length > 0
                ? 'Stock que rellena los dispensers. Podés dejarlo en 0.'
                : 'Stock desde el que va a comer la mascota (sin dispenser).'
            }
            min={0}
          />
        </div>
      </Card>

      <Card>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <h2 className="font-semibold text-slate-900">Dispensers</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {dispensers.length === 0
                ? 'Sin dispensers: la mascota come directo de la bolsa principal.'
                : `${dispensers.length} dispenser${dispensers.length === 1 ? '' : 's'} configurado${dispensers.length === 1 ? '' : 's'}.`}
            </p>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            leftIcon={Plus}
            onClick={addDispenser}
            disabled={
              submitting || dispensers.length >= VALIDATION.MAX_DISPENSERS_PER_GROUP
            }
          >
            Agregar
          </Button>
        </div>

        {errors.dispensers && (
          <p className="text-sm text-status-red bg-red-50 rounded-lg px-3 py-2 mb-3">
            {errors.dispensers}
          </p>
        )}

        {dispensers.length > 0 && (
          <div className="flex flex-col gap-3">
            {dispensers.map((d, idx) => {
              const capNum = Number(d.capacityGr);
              const initialPreview =
                capNum > 0 ? Math.round(capNum * d.initialLevel) : 0;
              const errs = dispErrorsMap[d.formId] || {};
              return (
                <div
                  key={d.formId}
                  className="rounded-xl border border-slate-200 p-3 flex flex-col gap-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-500">
                      Dispenser {idx + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeDispenser(d.formId)}
                      disabled={submitting}
                      aria-label={`Quitar dispenser ${idx + 1}`}
                      className="text-slate-400 hover:text-status-red transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <Input
                    label="Nombre"
                    placeholder="Ej: Cocina, Patio..."
                    value={d.name}
                    onChange={(e) => updateDispenser(d.formId, { name: e.target.value })}
                    error={errs.name}
                    maxLength={VALIDATION.DISPENSER_NAME_MAX}
                  />

                  <Input
                    label="Capacidad (gramos)"
                    type="number"
                    inputMode="numeric"
                    placeholder="Ej: 1000"
                    value={d.capacityGr}
                    onChange={(e) =>
                      updateDispenser(d.formId, { capacityGr: e.target.value })
                    }
                    error={errs.capacityGr}
                    hint="Cuánto entra cuando está lleno"
                    min={VALIDATION.DISPENSER_CAPACITY_MIN_GR}
                    max={VALIDATION.DISPENSER_CAPACITY_MAX_GR}
                  />

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">
                      ¿Cuán lleno está ahora?
                    </label>
                    <div className="flex gap-1.5">
                      {STOCK_LEVELS.map((level) => (
                        <button
                          key={level.value}
                          type="button"
                          onClick={() =>
                            updateDispenser(d.formId, { initialLevel: level.value })
                          }
                          className={[
                            'flex-1 rounded-xl border px-2 py-2 text-xs font-medium transition-colors',
                            d.initialLevel === level.value
                              ? 'border-brand-500 bg-brand-50 text-brand-800'
                              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                          ].join(' ')}
                        >
                          {level.label}
                        </button>
                      ))}
                    </div>
                    {capNum > 0 && (
                      <p className="mt-1.5 text-xs text-slate-500">
                        Equivale a {formatGrams(initialPreview)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="secondary"
          fullWidth
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="primary"
          fullWidth
          isLoading={submitting}
        >
          Crear grupo
        </Button>
      </div>
    </form>
  );
};

export default GroupForm;
